'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  question: string;
  answer: string;
  details: string[];
}

export default function PoliciesTermsSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      question: 'What are the check-in and check-out times?',
      answer: 'Standard check-in is at 11:00 AM and check-out is at 10:00 AM. Early/late options available with charges.',
      details: [
        'Check-in: 11:00 AM onwards (early check-in subject to availability)',
        'Check-out: 10:00 AM (late check-out available with charges)',
        'Early check-in (before 11:00 AM): 50% of daily rate',
        'Late check-out (10:00 AM - 6:00 PM): 50% of daily rate',
        'Late check-out (after 6:00 PM): Full day rate applies',
      ],
    },
    {
      question: 'What is your cancellation policy?',
      answer: 'We offer flexible cancellation with tiered refunds based on notice period, from 100% to no refund.',
      details: [
        '30+ days before check-in: 100% refund minus processing fee',
        '15-29 days before: 75% refund of total booking amount',
        '7-14 days before: 50% refund of total booking amount',
        'Less than 7 days: No refund - full amount charged',
        'Refunds processed within 7-10 business days',
      ],
    },
    {
      question: 'Do you allow pets?',
      answer: 'Yes, we are pet-friendly with advance approval and a refundable pet deposit.',
      details: [
        'Advance approval required (minimum 48 hours notice)',
        'Refundable pet deposit: ₹5,000 per pet',
        'Maximum 2 pets per apartment (weight limit: 15kg each)',
        'Pet cleaning fee: ₹1,500 per stay',
        'Pets must be leashed in common areas',
        'Vaccination records required at check-in',
      ],
    },
    {
      question: 'What is your smoking policy?',
      answer: 'All apartments and common areas are strictly non-smoking. Designated outdoor smoking areas available.',
      details: [
        'Smoking prohibited in all apartments and indoor areas',
        'Designated smoking areas available in outdoor spaces',
        'Smoking violation attracts a cleaning and restoration charge of Rs. 3,000',
        'Vaping and e-cigarettes subject to same policy',
      ],
    },
    {
      question: 'What identification is required at check-in?',
      answer: 'Valid government-issued photo ID is mandatory for all guests aged 18 and above.',
      details: [
        'Accepted IDs: Passport, Driving License, Aadhaar Card, Voter ID',
        'Foreign nationals must present passport and valid visa',
        'All guests must complete registration form',
        'Credit/debit card required for security deposit authorization',
      ],
    },
    {
      question: 'What is your guest conduct policy?',
      answer: 'We maintain a peaceful environment with quiet hours and strict visitor policies to ensure all guests enjoy their stay.',
      details: [
        'Quiet hours: 10:00 PM - 7:00 AM daily',
        'Visitors are not permitted in guest rooms',
        'All visitors must meet only in the lobby or designated areas',
        'Prior approval from the Front Desk is required for any visitor',
        'Management reserves the right to deny entry or request visitors to leave',
        'Parties and events prohibited without management approval',
        'Respectful behavior towards staff and other guests expected',
      ],
    },
  ];

  const toggleItem = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="policies-terms" className="py-16 lg:py-24 bg-background">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 text-accent mb-4">
            <Icon name="DocumentTextIcon" size={24} />
            <span className="text-sm font-semibold uppercase tracking-wide">Policies & Terms</span>
          </div>
          <h2 className="font-display text-3xl lg:text-5xl font-bold text-primary mb-4">
            Property Policies
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Important policies covering check-in procedures, cancellations, pet policies, and guest conduct guidelines.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-subtle border border-border overflow-hidden hover:shadow-elevated transition-all duration-300"
            >
              <button
                onClick={() => toggleItem(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-muted transition-colors duration-300"
              >
                <div className="flex-1 pr-4">
                  <h3 className="font-display text-xl font-semibold text-primary mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-text-secondary text-sm">{faq.answer}</p>
                </div>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  className="text-text-secondary flex-shrink-0"
                />
              </button>

              {openIndex === index && (
                <div className="px-6 pb-6 animate-slide-in-left">
                  <div className="border-t border-border pt-4">
                    <ul className="space-y-3">
                      {faq.details.map((detail, idx) => (
                        <li key={idx} className="flex items-start space-x-3">
                          <Icon
                            name="CheckCircleIcon"
                            size={20}
                            className="text-accent flex-shrink-0 mt-0.5"
                          />
                          <span className="text-text-secondary text-sm leading-relaxed">
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}